import React, { useEffect, useState } from 'react'
import { Typography, Row, Col, Card, Switch, Skeleton, Space, Input, Button, Checkbox, Select, Spin } from 'antd'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { dispatchAlert } from '../../Utilities/dispatchAlert'

const { Title, Paragraph } = Typography
const { Option } = Select

const DEFAULT_BUTTON_COLOR = '#6b4fbb'
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/

const reqConfig = token => ({
  headers: {
    authorization: `Bearer ${token}`,
  },
})

function StorefrontWidgetComponent() {
  const { token, storeBrands, storeCategories } = useSelector(state => state)
  const dispatch = useDispatch()
  const [enabled, setEnabled] = useState(false)
  const [buttonColor, setButtonColor] = useState(DEFAULT_BUTTON_COLOR)
  const [alwaysResidential, setAlwaysResidential] = useState(false)
  const [showResidentialCheckbox, setShowResidentialCheckbox] = useState(false)
  const [residentialCheckboxDefault, setResidentialCheckboxDefault] = useState(false)
  const [alwaysLiftgate, setAlwaysLiftgate] = useState(false)
  const [showLiftgateCheckbox, setShowLiftgateCheckbox] = useState(false)
  const [liftgateResidentialAuto, setLiftgateResidentialAuto] = useState(false)
  const [selectedProducts, setSelectedProducts] = useState([])
  const [selectedBrands, setSelectedBrands] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [productOptions, setProductOptions] = useState([])
  const [productSearch, setProductSearch] = useState('')
  const [searchingProducts, setSearchingProducts] = useState(false)
  const [loading, setLoading] = useState(true)
  const [savingToggle, setSavingToggle] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)

  useEffect(() => {
    if (!token) return
    axios
      .get(`${process.env.REACT_APP_ENITURE_API_URL}/widget_settings`, reqConfig(token))
      .then(({ data }) => {
        setEnabled(!!data.enabled)
        if (data.button_color) setButtonColor(data.button_color)
        setAlwaysResidential(!!data.always_residential)
        setShowResidentialCheckbox(!!data.show_residential_checkbox)
        setResidentialCheckboxDefault(!!data.residential_checkbox_default)
        setAlwaysLiftgate(!!data.always_liftgate)
        setShowLiftgateCheckbox(!!data.show_liftgate_checkbox)
        setLiftgateResidentialAuto(!!data.liftgate_residential_auto)
        setSelectedProducts(Array.isArray(data.products) ? data.products : [])
        setSelectedBrands(Array.isArray(data.filter_brands) ? data.filter_brands : [])
        setSelectedCategories(Array.isArray(data.filter_categories) ? data.filter_categories : [])
      })
      .finally(() => setLoading(false))
  }, [token])

  // Search products by name/SKU once the merchant has typed a few characters, matching the
  // same product-search endpoint the shipping rules page already uses.
  useEffect(() => {
    if (productSearch.trim().length <= 2) {
      setProductOptions([])
      return
    }
    let cancelled = false
    setSearchingProducts(true)
    axios
      .get(`${process.env.REACT_APP_ENITURE_API_URL}/get_shipping_rule_products`, {
        ...reqConfig(token),
        params: { search: productSearch },
      })
      .then(({ data }) => {
        // Ignore responses for a search term the merchant has since changed/cleared - without
        // this, a slower earlier request can resolve after a faster later one and stomp its
        // results, making correct matches appear to silently vanish from the dropdown.
        if (!cancelled) setProductOptions(Array.isArray(data.data) ? data.data : [])
      })
      .finally(() => {
        if (!cancelled) setSearchingProducts(false)
      })
    return () => {
      cancelled = true
    }
  }, [productSearch, token])

  const saveSettings = (payload, onDone) => {
    axios
      .post(`${process.env.REACT_APP_ENITURE_API_URL}/toggle_widget`, payload, reqConfig(token))
      .then(({ data }) => {
        if (!data.error) {
          setEnabled(!!data.enabled)
          if (data.button_color) setButtonColor(data.button_color)
          setAlwaysResidential(!!data.always_residential)
          setShowResidentialCheckbox(!!data.show_residential_checkbox)
          setResidentialCheckboxDefault(!!data.residential_checkbox_default)
          setAlwaysLiftgate(!!data.always_liftgate)
          setShowLiftgateCheckbox(!!data.show_liftgate_checkbox)
          setLiftgateResidentialAuto(!!data.liftgate_residential_auto)
          setSelectedProducts(Array.isArray(data.products) ? data.products : [])
          setSelectedBrands(Array.isArray(data.filter_brands) ? data.filter_brands : [])
          setSelectedCategories(Array.isArray(data.filter_categories) ? data.filter_categories : [])
          dispatch(dispatchAlert(true, 'success', 'Settings saved successfully.'))
        } else {
          dispatch(dispatchAlert(true, 'error', data.message))
        }
      })
      .catch(() => {
        dispatch(dispatchAlert(true, 'error', 'Failed to update the widget setting.'))
      })
      .finally(onDone)
  }

  const handleToggle = checked => {
    setSavingToggle(true)
    saveSettings(
      {
        enabled: checked,
        button_color: buttonColor,
        always_residential: alwaysResidential,
        show_residential_checkbox: showResidentialCheckbox,
        residential_checkbox_default: residentialCheckboxDefault,
        always_liftgate: alwaysLiftgate,
        show_liftgate_checkbox: showLiftgateCheckbox,
        liftgate_residential_auto: liftgateResidentialAuto,
        products: selectedProducts,
        filter_brands: selectedBrands,
        filter_categories: selectedCategories,
      },
      () => setSavingToggle(false)
    )
  }

  const handleSaveSettings = () => {
    if (!HEX_COLOR_REGEX.test(buttonColor)) {
      dispatch(dispatchAlert(true, 'error', 'Enter a valid hex color, e.g. #6b4fbb.'))
      return
    }
    setSavingSettings(true)
    saveSettings(
      {
        enabled,
        button_color: buttonColor,
        always_residential: alwaysResidential,
        show_residential_checkbox: showResidentialCheckbox,
        residential_checkbox_default: residentialCheckboxDefault,
        always_liftgate: alwaysLiftgate,
        show_liftgate_checkbox: showLiftgateCheckbox,
        liftgate_residential_auto: liftgateResidentialAuto,
        products: selectedProducts,
        filter_brands: selectedBrands,
        filter_categories: selectedCategories,
      },
      () => setSavingSettings(false)
    )
  }

  if (loading) return <Skeleton active />

  return (
    <Row gutter={24} justify="center">
      <Col className="gutter-row mb-3" xs={24} sm={24} md={24} lg={24} xl={22}>
        <Title level={3}>Product Page Shipping Calculator</Title>
      </Col>
      <Col className="gutter-row" xs={24} sm={24} md={24} lg={24} xl={22}>
        <Card style={{ width: '100%' }}>
          <Paragraph>
            Display a real-time shipping calculator on product pages, allowing customers to
            estimate shipping costs before adding items to their cart. Shipping rates are
            calculated using your existing carrier and shipping configurations, providing
            customers with accurate shipping estimates early in their shopping journey.
          </Paragraph>
          <Space align="center">
            <Switch
              style={{ backgroundColor: enabled ? '#479ccf' : undefined }}
              checked={enabled}
              loading={savingToggle}
              onChange={handleToggle}
            />
            <span>Enable Product Page Shipping Calculator</span>
          </Space>
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            Display a shipping rate calculator on product pages so customers can estimate shipping
            costs before adding products to their cart.
          </Paragraph>
          {!enabled && (
            <Paragraph type="secondary" style={{ fontStyle: 'italic', marginTop: 8, marginBottom: 0 }}>
              The Product Page Shipping Calculator is currently disabled and will not appear on
              storefront product pages.
            </Paragraph>
          )}

          <div style={{ marginTop: 24 }}>
            <Paragraph style={{ marginBottom: 8 }}>
              <strong>Calculate Shipping Button Color</strong>
            </Paragraph>
            <Paragraph type="secondary" style={{ marginTop: -8, marginBottom: 8 }}>
              Choose the color used for the Calculate Shipping button displayed on product pages.
            </Paragraph>
            <Space align="center">
              <input
                type="color"
                value={HEX_COLOR_REGEX.test(buttonColor) ? buttonColor : DEFAULT_BUTTON_COLOR}
                onChange={e => setButtonColor(e.target.value)}
                disabled={!enabled}
                style={{
                  width: 40,
                  height: 32,
                  padding: 0,
                  border: '1px solid #d9d9d9',
                  cursor: enabled ? 'pointer' : 'not-allowed',
                }}
              />
              <Input
                value={buttonColor}
                onChange={e => setButtonColor(e.target.value)}
                style={{ width: 120 }}
                maxLength={7}
                disabled={!enabled}
              />
            </Space>
          </div>

          <div style={{ marginTop: 24 }}>
            <Paragraph style={{ marginBottom: 8 }}>
              <strong>Address Type Settings</strong>
            </Paragraph>
            <Space direction="vertical">
              <Checkbox
                checked={alwaysResidential}
                onChange={e => setAlwaysResidential(e.target.checked)}
                disabled={!enabled}
              >
                Always quote as a residential address
              </Checkbox>
              <Checkbox
                checked={showResidentialCheckbox}
                onChange={e => setShowResidentialCheckbox(e.target.checked)}
                disabled={!enabled || alwaysResidential}
              >
                Include a checkbox for residential address
              </Checkbox>
              <Checkbox
                checked={residentialCheckboxDefault}
                onChange={e => setResidentialCheckboxDefault(e.target.checked)}
                disabled={!enabled || alwaysResidential || !showResidentialCheckbox}
                style={{ marginLeft: 24 }}
              >
                Enable the checkbox for residential address by default
              </Checkbox>
            </Space>
          </div>

          <div style={{ marginTop: 24 }}>
            <Paragraph style={{ marginBottom: 8 }}>
              <strong>Liftgate Delivery Settings (LTL freight quotes only)</strong>
            </Paragraph>
            <Space direction="vertical">
              <Checkbox
                checked={alwaysLiftgate}
                onChange={e => {
                  const checked = e.target.checked
                  setAlwaysLiftgate(checked)
                  if (checked) setLiftgateResidentialAuto(false)
                }}
                disabled={!enabled}
              >
                Always include the fee for liftgate delivery
              </Checkbox>
              <Checkbox
                checked={showLiftgateCheckbox}
                onChange={e => setShowLiftgateCheckbox(e.target.checked)}
                disabled={!enabled || alwaysLiftgate}
              >
                Include a checkbox for liftgate delivery
              </Checkbox>
              <Checkbox
                checked={liftgateResidentialAuto}
                onChange={e => setLiftgateResidentialAuto(e.target.checked)}
                disabled={!enabled || alwaysLiftgate}
                style={{ marginLeft: 24 }}
              >
                Always include the liftgate delivery fee when quoting a residential address
              </Checkbox>
            </Space>
          </div>

          <div style={{ marginTop: 24 }}>
            <Paragraph style={{ marginBottom: 8 }}>
              <strong>Filter Products</strong>
            </Paragraph>
            <Paragraph type="secondary" style={{ marginTop: -8, marginBottom: 8 }}>
              Choose which products display the shipping calculator by Brand, Category, and/or
              Product. A product is shown if it matches any of the selected criteria below. Leave
              everything blank to show it on every product page.
            </Paragraph>

            <Paragraph style={{ marginBottom: 4 }}>Categories</Paragraph>
            <Select
              mode="multiple"
              style={{ width: '100%', marginBottom: 16 }}
              placeholder="Select categories"
              value={selectedCategories}
              disabled={!enabled}
              onChange={setSelectedCategories}
              filterOption={(input, option) =>
                (option.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {(storeCategories || []).map(category => (
                <Option key={category.key} value={category.key}>
                  {category.value}
                </Option>
              ))}
            </Select>
            
            <Paragraph style={{ marginBottom: 4 }}>Brands</Paragraph>
            <Select
              mode="multiple"
              style={{ width: '100%', marginBottom: 16 }}
              placeholder="Select brands"
              value={selectedBrands}
              disabled={!enabled}
              onChange={setSelectedBrands}
              filterOption={(input, option) =>
                (option.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {(storeBrands || []).map(brand => (
                <Option key={brand.key} value={brand.key}>
                  {brand.value}
                </Option>
              ))}
            </Select>

            <Paragraph style={{ marginBottom: 4 }}>Products</Paragraph>
            <Select
              mode="multiple"
              labelInValue
              style={{ width: '100%' }}
              placeholder="Search products by name, SKU"
              value={selectedProducts}
              disabled={!enabled}
              filterOption={false}
              onSearch={setProductSearch}
              onChange={setSelectedProducts}
              notFoundContent={
                searchingProducts ? (
                  <Spin size="small" />
                ) : productSearch.trim().length > 2 ? (
                  'No products found.'
                ) : (
                  'Enter at least 3 characters to search.'
                )
              }
            >
              {productOptions.map(product => (
                <Option key={product.source_product_id ?? product.id} value={product.source_product_id ?? product.id}>
                  {product.name}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ marginTop: 24 }}>
            <Button type="primary" loading={savingSettings} onClick={handleSaveSettings} disabled={!enabled}>
              Save Settings
            </Button>
          </div>
        </Card>
      </Col>
    </Row>
  )
}

export default StorefrontWidgetComponent
